/**
 * The studios page (container, D25's admin screen): the list and the create
 * dialog. "From the template" builds the config from the one template source
 * (`@acp/engine/studioTemplate.js`, what `acp studio-template` prints), on
 * the simulator host and port block the page's `?simHost=&simBasePort=` name
 * (`pnpm dev:studio` prints that link) or the primary checkout's.
 */
import { studioTemplate } from '@acp/engine/studioTemplate.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateStudio, useGrants, useStudios } from '../app/hooks.js';
import { CreateStudioDialog } from '../components/studios/CreateStudioDialog.js';
import { StudioList, studioHref } from '../components/studios/StudioList.js';
import { allowsSomewhere } from '../lib/permissions.js';
import { simDefaultsFrom } from '../lib/studio/simDefaults.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function StudiosView() {
  const studios = useStudios();
  const create = useCreateStudio();
  const grants = useGrants();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sim] = useState(() => simDefaultsFrom(typeof window === 'undefined' ? '' : window.location.search));
  return (
    <div className="mx-auto grid max-w-5xl gap-6">
      <StudioList
        studios={studios.data ?? []}
        loading={studios.isFetching}
        error={studios.error === null ? null : messageOf(studios.error)}
        canCreate={allowsSomewhere(grants, 'device.configure')}
        onCreate={() => {
          create.reset();
          setOpen(true);
        }}
        onRetry={() => {
          void studios.refetch();
        }}
        onOpen={(studioId) => {
          void navigate({ to: studioHref(studioId) });
        }}
      />
      <CreateStudioDialog
        open={open}
        onOpenChange={setOpen}
        defaultHost={sim.host}
        defaultBasePort={sim.basePort}
        pending={create.isPending}
        error={create.error === null ? null : messageOf(create.error)}
        onSubmit={(request) => {
          const config = request.template === null ? {} : studioTemplate(request.template.host, request.template.basePort);
          create.mutate(
            { studioId: request.studioId, name: request.name, siteId: request.siteId, config },
            {
              onSuccess: (studio) => {
                setOpen(false);
                toast.success(`Created “${studio.name}”`);
                void navigate({ to: studioHref(studio.studioId) });
              },
            },
          );
        }}
      />
    </div>
  );
}
